/**
 * THE END-OF-UPLOAD SUMMARY (owner ask 2026-10-22): after a big drop — 2,413
 * files, 760 of them new and 1,653 exact duplicates of files already in the room
 * — the screen must say so plainly, or the person thinks files went missing.
 * Built from what the finalize answers already return (201 = added; 200 +
 * `skipped` = the room already held these exact bytes, ADR-0028; a rejected
 * request = failed) and the file names this browser holds. Shared by the admin's
 * upload panel and the seller's submit page.
 */

/** How one picked file ended up (or `pending` while it is queued or in flight). */
export type UploadOutcomeKind = 'added' | 'skipped' | 'failed' | 'pending';

/** One picked file and its outcome. */
export interface UploadOutcome {
  name: string;
  kind: UploadOutcomeKind;
  /** Why it failed ("File is larger than the 50 MB limit."), when it did. */
  detail?: string;
}

export interface UploadBatchSummary {
  /** Every picked file. */
  total: number;
  added: number;
  skipped: number;
  failed: number;
  /** Still queued or uploading. */
  pending: number;
  /** Nothing pending: the summary is final. */
  done: boolean;
  skippedNames: string[];
  failures: { name: string; detail: string | null }[];
}

/** Counts a batch. Pure. */
export function summarizeUploads(outcomes: readonly UploadOutcome[]): UploadBatchSummary {
  const summary: UploadBatchSummary = {
    total: outcomes.length,
    added: 0,
    skipped: 0,
    failed: 0,
    pending: 0,
    done: false,
    skippedNames: [],
    failures: [],
  };
  for (const o of outcomes) {
    if (o.kind === 'added') summary.added += 1;
    else if (o.kind === 'skipped') {
      summary.skipped += 1;
      summary.skippedNames.push(o.name);
    } else if (o.kind === 'failed') {
      summary.failed += 1;
      summary.failures.push({ name: o.name, detail: o.detail ?? null });
    } else summary.pending += 1;
  }
  summary.done = summary.total > 0 && summary.pending === 0;
  return summary;
}

const count = (n: number) => n.toLocaleString('en-US');

/**
 * The summary sentence: "2,413 files: 760 added, 1,653 skipped as exact
 * duplicates of files already in the room, 2 failed to upload." Parts that are
 * zero are left out (but "added" always stands). Pure.
 */
export function uploadSummarySentence(s: UploadBatchSummary): string {
  const parts = [`${count(s.added)} added`];
  if (s.skipped > 0) {
    parts.push(`${count(s.skipped)} skipped as exact duplicates of files already in the room`);
  }
  if (s.failed > 0) parts.push(`${count(s.failed)} failed to upload`);
  return `${count(s.total)} ${s.total === 1 ? 'file' : 'files'}: ${parts.join(', ')}.`;
}
